/* eslint-disable max-lines-per-function */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { DateTime } from "luxon";
import ListHeader from "../../../components/tables/ListHeader";
import Loader from "../../../components/Loader";
import GenericAlert from "../../../components/GenericAlert";
import { IAlert } from "../../../interfaces/alert";
import { ICoverageSummary, IDemandCoverageSummary } from "../../../interfaces/coverage";
import { getMatrixCoverage, getMatrixCoverageDemand } from "../../../requests/coverageRequests";
import {
  calculateMatrixCompletionRate,
  listAffectedOrders,
  listUncoveredDestinationGaps,
  listWeightGaps,
} from "../../../utils/coverageAggregation";
import { affectedOrdersToCsv, coverageGapsToCsv, downloadCsv } from "../../../utils/csvExport";
import { formatNumber } from "../../../utils/metricsFormatters";
import { TSeverity } from "../../../utils/MetricThresholds";
import { COVERAGE_GAP_REASON_BY_TYPE, ERROR, MEDIUM } from "../../../utils/constants";
import { AUDIT_TAB_VALUES } from "../../../components/tabs/AuditsTabs";

const COVERAGE_TABS = {
  DESTINATIONS: "destinations",
  WEIGHTS: "weights",
  AFFECTED_ORDERS: "affected_orders",
};

// El header sticky de MUI fuerza un fondo blanco propio; lo pisamos para que
// quede gris claro (bg-light) y el cuerpo de la tabla blanco, como en Figma.
const stickyHeaderSx = { "& .MuiTableCell-stickyHeader": { backgroundColor: "var(--bg-light)" } };

const UncoveredDestinationsSection = ({ summary }: { summary: ICoverageSummary }) => {
  const gaps = useMemo(() => listUncoveredDestinationGaps(summary), [summary]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} className="m-t-20">
        <Typography className="f-s-14" sx={{ flex: 1, minWidth: 260 }}>
          Estos códigos postales consultados no encuentran un rango de destino válido en la matriz logística
          configurada.
        </Typography>
        {gaps.length > 0 && (
          <Button
            variant="contained"
            size="small"
            sx={{ padding: "8px" }}
            onClick={() => downloadCsv("destinos-sin-cobertura.csv", coverageGapsToCsv(gaps))}
          >
            {`Exportar CSV (${gaps.length} filas)`}
          </Button>
        )}
      </Box>
      {gaps.length === 0 ? (
        <Typography className="f-s-14 m-t-20 text-dark">No se detectaron destinos sin cobertura.</Typography>
      ) : (
        <TableContainer component={Paper} className="m-t-20 br-20" sx={{ maxHeight: 480, overflow: "auto" }}>
          <Table stickyHeader sx={stickyHeaderSx}>
            <ListHeader columns={[{ label: "Origen" }, { label: "Destino" }, { label: "Motivo" }]} />
            <TableBody>
              {gaps.map((gap) => (
                <TableRow key={`${gap.origin}-${gap.destinationFrom}-${gap.destinationTo}`}>
                  <TableCell>{gap.origin}</TableCell>
                  <TableCell>{`${gap.destinationFrom} - ${gap.destinationTo}`}</TableCell>
                  <TableCell className="color-error">{COVERAGE_GAP_REASON_BY_TYPE[gap.type]}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

const WeightGapsSection = ({ summary }: { summary: ICoverageSummary }) => {
  const gaps = useMemo(() => listWeightGaps(summary), [summary]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} className="m-t-20">
        <Typography className="f-s-14" sx={{ flex: 1, minWidth: 260 }}>
          Rangos de peso sin cobertura detectados entre bandas configuradas para un mismo origen y destino.
        </Typography>
        {gaps.length > 0 && (
          <Button
            variant="contained"
            size="small"
            sx={{ padding: "8px" }}
            onClick={() => downloadCsv("huecos-de-peso.csv", coverageGapsToCsv(gaps))}
          >
            {`Exportar CSV (${gaps.length} filas)`}
          </Button>
        )}
      </Box>
      {gaps.length === 0 ? (
        <Typography className="f-s-14 m-t-20 text-dark">Sin huecos de peso detectados.</Typography>
      ) : (
        <TableContainer component={Paper} className="m-t-20 br-20" sx={{ maxHeight: 480, overflow: "auto" }}>
          <Table stickyHeader sx={stickyHeaderSx}>
            <ListHeader columns={[{ label: "Origen" }, { label: "Destino" }, { label: "Peso sin cobertura" }]} />
            <TableBody>
              {gaps.map((gap) => (
                <TableRow key={`${gap.origin}-${gap.destinationFrom}-${gap.destinationTo}-${gap.weightFrom}`}>
                  <TableCell>{gap.origin}</TableCell>
                  <TableCell>{`${gap.destinationFrom} - ${gap.destinationTo}`}</TableCell>
                  <TableCell className="color-error">{`${gap.weightFrom}-${gap.weightTo} kg`}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

const AffectedOrdersSection = ({ summary }: { summary: IDemandCoverageSummary }) => {
  const orders = useMemo(() => listAffectedOrders(summary), [summary]);

  return (
    <>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} className="m-t-20">
        <Typography className="f-s-14" sx={{ flex: 1, minWidth: 260 }}>
          Pedidos de los últimos 30 días que no encontraron una cobertura válida en la matriz.
        </Typography>
        {orders.length > 0 && (
          <Button
            variant="contained"
            size="small"
            sx={{ padding: "8px" }}
            onClick={() => downloadCsv("envios-afectados.csv", affectedOrdersToCsv(orders))}
          >
            {`Exportar CSV (${orders.length} filas)`}
          </Button>
        )}
      </Box>
      {orders.length === 0 ? (
        <Typography className="f-s-14 m-t-20 text-dark">Sin envíos afectados en los últimos 30 días.</Typography>
      ) : (
        <TableContainer component={Paper} className="m-t-20 br-20" sx={{ maxHeight: 480, overflow: "auto" }}>
          <Table stickyHeader sx={stickyHeaderSx}>
            <ListHeader columns={[{ label: "ID externo" }, { label: "N° de guía" }]} />
            <TableBody>
              {orders.map((order) => (
                <TableRow key={`${order.external_id}-${order.tracking_id}`}>
                  <TableCell>{order.external_id}</TableCell>
                  <TableCell>{order.tracking_id}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </>
  );
};

const CoverageRateCard = ({
  title,
  value,
  caption,
  severity,
}: {
  title: string;
  value: string;
  caption?: string;
  severity?: TSeverity;
}) => {
  const theme = useTheme();

  return (
    <Paper className="br-20 bg-light" sx={{ p: 2.5, flex: "1 1 220px" }}>
      <Typography className="f-s-14 text-dark">{title}</Typography>
      <Typography className="f-s-20" sx={{ fontWeight: 600, color: severity ? theme.palette[severity].main : undefined }}>
        {value}
      </Typography>
      {caption && (
        <Typography className="f-s-14 text-dark m-t-10">{caption}</Typography>
      )}
    </Paper>
  );
};

const GapHighlightCard = ({
  icon,
  title,
  value,
  caption,
}: {
  icon: string;
  title: string;
  value: number;
  caption: string;
}) => (
  <Paper className="br-20 bg-light" sx={{ p: 2.5, flex: "1 1 320px" }}>
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        className="color-error"
        sx={{ width: 32, height: 32, borderRadius: "50%", bgcolor: "rgba(217, 0, 1, 0.1)" }}
      >
        <span className="material-symbols-rounded f-s-18">{icon}</span>
      </Box>
      <Typography className="f-s-14 text-dark">{title}</Typography>
    </Box>
    <Typography className="f-s-20 color-error m-t-10" sx={{ fontWeight: 600 }}>
      {formatNumber(value)}
    </Typography>
    <Typography className="f-s-14 text-dark m-t-10">{caption}</Typography>
  </Paper>
);

const MatrixCoverage = () => {
  const navigate = useNavigate();
  const { matrixId } = useParams<{ matrixId: string }>();
  const [searchParams] = useSearchParams();
  const credentialId = searchParams.get("credential_id");

  const [alert, setAlert] = useState<IAlert>({} as IAlert);
  const [loading, setLoading] = useState<boolean>(true);
  const [coverage, setCoverage] = useState<ICoverageSummary | null>(null);
  const [demandCoverage, setDemandCoverage] = useState<IDemandCoverageSummary | null>(null);
  const [tab, setTab] = useState<string>(COVERAGE_TABS.DESTINATIONS);

  const matrixCompletionRate = useMemo(
    () => (coverage ? calculateMatrixCompletionRate(coverage) : null),
    [coverage]
  );
  const uncoveredDestinationsCount = useMemo(
    () => (coverage ? listUncoveredDestinationGaps(coverage).length : 0),
    [coverage]
  );
  const weightGapsCount = useMemo(() => (coverage ? listWeightGaps(coverage).length : 0), [coverage]);

  useEffect(() => {
    const loadCoverage = async () => {
      if (!matrixId || !credentialId) {
        setAlert({ typeOfAlert: ERROR, message: "Faltan datos para consultar la cobertura de esta matriz.", showAlert: true });
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const to = DateTime.now();
        const from = to.minus({ days: 30 });
        const [coverageResponse, demandResponse] = await Promise.all([
          getMatrixCoverage(matrixId, credentialId),
          getMatrixCoverageDemand(matrixId, credentialId, from.toISO() || "", to.toISO() || ""),
        ]);
        setCoverage(coverageResponse);
        setDemandCoverage(demandResponse);
        setAlert({} as IAlert);
      } catch (error: any) {
        setAlert({ typeOfAlert: ERROR, message: error, showAlert: true });
      } finally {
        setLoading(false);
      }
    };
    loadCoverage();
  }, [matrixId, credentialId]);

  if (loading) return <Loader size={MEDIUM} />;

  return (
    <Grid container>
      <Grid item xs={12} display="flex" alignItems="center" mb={0.5}>
        <IconButton onClick={() => navigate(`../?tab=${AUDIT_TAB_VALUES.MATRICES}`)}>
          <span className="material-symbols-rounded text-dark">keyboard_backspace</span>
        </IconButton>
        <Typography className="f-s-20 text-dark" ml={1}>
          Cobertura de matriz
        </Typography>
      </Grid>
      <Grid item xs={12} mb={2.5}>
        <Typography className="f-s-14 text-dark" ml={6}>
          Detectá problemas de cobertura en la matriz que pueden generar fallback al calcular el envío.
        </Typography>
      </Grid>

      {alert.showAlert && (
        <Grid item xs={12} mb={2.5}>
          <GenericAlert alert={alert} />
        </Grid>
      )}

      {!alert.showAlert && coverage && demandCoverage && (
        <Grid item xs={12}>
          <Alert severity="error" className="m-b-20">
            Los problemas detectados pueden generar fallback al no encontrar una tarifa válida para una combinación
            de destino y peso.
          </Alert>

          <CoverageRateCard
            title="Estado de configuración"
            value={matrixCompletionRate === null ? "Sin datos" : `${matrixCompletionRate.toFixed(0)}% de completitud`}
            caption={`Se detectaron filas válidas y configuraciones con posibles huecos de peso o destino.
              Es un indicador de calidad de la matriz, no de cobertura logística.`}
          />

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }} className="m-t-20">
            <GapHighlightCard
              icon="wrong_location"
              title="Destinos sin cobertura"
              value={uncoveredDestinationsCount}
              caption="CP consultados que no encuentran un rango de destino configurado."
            />
            <GapHighlightCard
              icon="monitor_weight"
              title="Huecos de peso"
              value={weightGapsCount}
              caption="Rangos de peso sin cobertura detectados."
            />
          </Box>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }} className="m-t-20">
            <CoverageRateCard
              title="Filas válidas"
              value={formatNumber(coverage.validRowCount)}
              caption="Filas de la matriz con una tarifa correctamente configurada."
            />
            <CoverageRateCard
              title="Orígenes"
              value={formatNumber(coverage.originsFound)}
              caption="Orígenes configurados en la matriz."
            />
            <CoverageRateCard
              title="Rangos de destino"
              value={formatNumber(coverage.destinationRangesFound)}
              caption="Rangos de destino configurados."
            />
          </Box>

          <TabContext value={tab}>
            <Box className="m-t-30" sx={{ borderBottom: 1, borderColor: "divider", width: "fit-content" }}>
              <TabList onChange={(_event, newValue) => setTab(newValue)}>
                <Tab label="Destinos sin cobertura" value={COVERAGE_TABS.DESTINATIONS} />
                <Tab label="Huecos de peso" value={COVERAGE_TABS.WEIGHTS} />
                <Tab label="Envíos afectados" value={COVERAGE_TABS.AFFECTED_ORDERS} />
              </TabList>
            </Box>

            <TabPanel value={COVERAGE_TABS.DESTINATIONS}>
              <UncoveredDestinationsSection summary={coverage} />
            </TabPanel>
            <TabPanel value={COVERAGE_TABS.WEIGHTS}>
              <WeightGapsSection summary={coverage} />
            </TabPanel>
            <TabPanel value={COVERAGE_TABS.AFFECTED_ORDERS}>
              <AffectedOrdersSection summary={demandCoverage} />
            </TabPanel>
          </TabContext>
        </Grid>
      )}
    </Grid>
  );
};

export default MatrixCoverage;
